import { LaravelResponse } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { differenceInMilliseconds } from "date-fns";
import { useEffect, useMemo } from "react";
import { AnyMenuItem } from "../shared/types";
import { getMenuItemUrl } from "./getMenuItemUrl";

const MAX_TIMEOUT = 2147483647;

const flattenMenuItems = <T extends { children: T[] }>(items: T[]): T[] =>
	items.reduce((acc, cur) => [...acc, cur, ...(cur.children.length ? flattenMenuItems(cur.children) : [])], [] as T[]);

const sortMenuItems = (items: AnyMenuItem[]): AnyMenuItem[] =>
	items.sort((a, b) => {
		if (!a.previewUntil || !b.previewUntil) return -1;
		return +b.previewUntil - +a.previewUntil;
	});

const buildMenuItems = (items: AnyMenuItem[]): AnyMenuItem[] => {
	const now = new Date();
	return items.map((x) => {
		const url = getMenuItemUrl(x);
		if (x.previewUntil && new Date(x.previewUntil) > now) {
			return {
				...x,
				url,
				children: buildMenuItems(x.children),
			} as AnyMenuItem;
		} else {
			if (x.type === "CATEGORY") {
				return {
					...x,
					children: buildMenuItems(x.children),
				};
			}
			return {
				...x,
				url,
				children: buildMenuItems(x.children),
			} as AnyMenuItem;
		}
	});
};

export const usePreviewMenuItems = (path: string) => {
	const { data, refetch } = useQuery({
		queryKey: ["preview-menu-item", path],
		queryFn: () => axios.get<LaravelResponse<AnyMenuItem[]>>(path).then((response) => response.data.data),
	});
	const menuItems = useMemo(() => buildMenuItems(data || []), [data]);

	useEffect(() => {
		let timeout: number | null = null;
		if (data == null || data.length === 0) return;

		const now = new Date();
		const menuItemsWithPreviewUntil = sortMenuItems(
			flattenMenuItems(data).filter((x) => x.previewUntil && new Date(x.previewUntil) > now),
		);
		const nextEndingMenuItem = menuItemsWithPreviewUntil.length ? menuItemsWithPreviewUntil[0] : null;

		if (nextEndingMenuItem && nextEndingMenuItem.previewUntil) {
			const milliseconds = Math.min(
				differenceInMilliseconds(new Date(nextEndingMenuItem.previewUntil), now),
				MAX_TIMEOUT,
			);
			timeout = window.setTimeout(refetch, milliseconds);
		}

		return () => {
			timeout && window.clearTimeout(timeout);
		};
	}, [data, refetch]);

	return menuItems;
};
